<template>
  <div class="pc-page page-body">
    <div class="pc-group-hero">
      <CategoryBanner
        full
        :tone="groupVisual.tone"
        :icon="groupVisual.icon"
        eyebrow="Product Center"
        :title="activeGroupMeta.title"
        :desc="activeGroupMeta.desc"
        :tags="groupVisual.tags"
        :background="groupVisual.background"
        :bg-size="groupVisual.bgSize"
        :bg-position="groupVisual.bgPosition"
        :bg-color="groupVisual.bgColor"
      />
    </div>

    <!-- 子导航：面包屑 + 锚点 -->
    <div class="pc-subnav">
      <div class="pc-subnav__inner">
        <nav class="pc-breadcrumb" aria-label="面包屑">
          <a href="#" class="pc-breadcrumb__link" @click.prevent="goHome">主页</a>
          <span class="pc-breadcrumb__sep">/</span>
          <span class="pc-breadcrumb__current">{{ activeGroupMeta.title }}</span>
        </nav>
        <div class="pc-subnav__links">
          <a href="#" class="pc-subnav__link" @click.prevent="scrollToSection('pc-features')">特点</a>
          <a href="#" class="pc-subnav__link" @click.prevent="scrollToSection('pc-products')">产品</a>
        </div>
      </div>
    </div>

    <!-- 一级分类 Tab（居中） -->
    <div class="pc-tabs-bar">
      <div class="pc-tabs-bar__inner filter-row">
        <button
          v-for="group in productSidebar"
          :key="group.id"
          type="button"
          class="pc-tabs-bar__tab filter-chip"
          :class="{ 'pc-tabs-bar__tab--active filter-chip--active': activeGroupId === group.id }"
          @click="switchGroup(group.id)"
        >
          {{ group.label }}
        </button>
      </div>
    </div>

    <!-- 产品区：左侧子分类 + 右侧网格 -->
    <section id="pc-products" class="pc-explorer">
      <div class="pc-explorer__inner">
        <aside class="pc-filter-sidebar">
          <button type="button" class="pc-compare-btn">产品对比</button>
          <ul class="pc-filter-list">
            <li v-for="item in activeGroupCategories" :key="item.id">
              <button
                type="button"
                class="pc-filter-item"
                :class="{ 'pc-filter-item--active': activeCategoryId === item.id }"
                @click="selectCategory(item.id)"
              >
                {{ item.label }}
              </button>
            </li>
          </ul>
        </aside>

        <main class="pc-product-area">
          <header class="pc-category-head">
            <div class="pc-category-head__copy">
              <span class="pc-category-head__eyebrow">{{ category.parentLabel }}</span>
              <h2 class="pc-category-head__title">{{ category.label }}</h2>
              <p v-if="category.heroDesc" class="pc-category-head__desc">{{ category.heroDesc }}</p>
            </div>
            <div class="pc-category-head__meta">
              <span class="pc-category-head__count">{{ filteredProducts.length }} 项产品 / 方案</span>
              <a href="#" class="pc-category-head__link" @click.prevent="scrollToSection('pc-features')">
                查看分类特点
              </a>
            </div>
          </header>

          <!-- 特点展示：跟随当前子分类 -->
          <section id="pc-features" class="pc-feature-panel">
            <div class="pc-feature-panel__head">
              <div>
                <span class="section-eyebrow">Category Highlights</span>
                <h3 class="pc-feature-panel__title">分类特点与能力说明</h3>
              </div>
              <p class="pc-feature-panel__hint">切换下方维度，了解该子分类的核心能力、功能范围与应用场景</p>
            </div>

            <div class="pc-feature-body">
              <div class="pc-feature-tabs" role="tablist">
                <button
                  v-for="(feat, idx) in category.features"
                  :key="feat.label"
                  type="button"
                  role="tab"
                  class="pc-feature-tab"
                  :class="{ 'pc-feature-tab--active': activeFeatureIdx === idx }"
                  :aria-selected="activeFeatureIdx === idx"
                  @click="activeFeatureIdx = idx"
                >
                  <span class="pc-feature-tab__icon">
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                      <path :d="featureIconPaths[feat.icon]" />
                    </svg>
                  </span>
                  <span>{{ feat.label }}</span>
                </button>
              </div>

              <div :key="activeFeatureIdx" class="pc-feature-content">
                <div class="pc-feature-content__main">
                  <h4 class="pc-feature-content__title">{{ activeFeature.label }}</h4>
                  <p v-if="activeFeature.desc" class="pc-feature-content__desc">{{ activeFeature.desc }}</p>
                  <ul class="pc-feature-bullets">
                    <li v-for="(bullet, i) in activeFeature.bullets" :key="i">{{ bullet }}</li>
                  </ul>
                </div>
                <aside v-if="activeFeature.tags?.length" class="pc-feature-aside">
                  <p class="pc-feature-aside__label">相关关键词</p>
                  <ul class="pc-feature-tags">
                    <li v-for="tag in activeFeature.tags" :key="tag">{{ tag }}</li>
                  </ul>
                </aside>
              </div>
            </div>
          </section>

          <!-- 子分类 Tab（产品标签筛选） -->
          <div v-if="category.featureTabs.length > 1" class="pc-subtabs">
            <button
              v-for="tab in category.featureTabs"
              :key="tab"
              type="button"
              class="pc-subtab"
              :class="{ 'pc-subtab--active': activeTab === tab }"
              @click="activeTab = tab"
            >
              {{ tab }}
            </button>
          </div>

          <div class="pc-product-grid">
            <article
              v-for="(product, idx) in filteredProducts"
              :key="idx"
              class="pc-card"
              :class="{
                'pc-card--system': product.productKind === 'system',
                'pc-card--ui': isProductUiCover(product),
              }"
              role="button"
              tabindex="0"
              @click="goProductDetail(product)"
              @keydown.enter="goProductDetail(product)"
            >
              <div class="pc-card__media">
                <img
                  :src="product.image"
                  :alt="product.name"
                  class="pc-card__img"
                  :class="{ 'pc-card__img--focus': product.listImageFocus }"
                  :style="listCardImgStyle(product)"
                  @error="(e) => onProductImgError(e, product)"
                />
              </div>
              <div class="pc-card__body">
                <h3 class="pc-card__name">{{ product.name }}</h3>
                <p class="pc-card__desc">{{ product.desc }}</p>
                <p class="pc-card__model">{{ product.model }}</p>
              </div>
            </article>
          </div>
        </main>
      </div>
    </section>

    <button
      v-if="showShelfFab"
      type="button"
      class="pc-shelf-fab"
      title="上架新产品"
      @click="goProductAdmin"
    >
      <span class="pc-shelf-fab__icon">＋</span>
      <span class="pc-shelf-fab__text">上架</span>
    </button>
  </div>
</template>

<script setup>
import CategoryBanner from '../components/CategoryBanner.vue'
import { ref, computed, watch, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
  productSidebar,
  getCategoryById,
  defaultCategoryId,
} from '../data/productCatalog'
import { getMergedCategoryProducts } from '../utils/customProductCatalog'
import { CLIENT_DEMO_MODE } from '../config/demoMode'
import { getGroupVisual, getGroupTags, groupDescriptions } from '../data/categoryVisuals'
import { useScrollReveal } from '../composables/useScrollReveal'

const route = useRoute()
const router = useRouter()
const { refreshReveal } = useScrollReveal(route)

const activeCategoryId = ref(defaultCategoryId)
const activeGroupId = ref('system')
const activeTab = ref('')
const activeFeatureIdx = ref(0)

const featureIconPaths = {
  controller: 'M4 6h16v12H4V6zm2 2v8h12V8H6zm2 2h8v4H8v-4z',
  vision:
    'M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z',
  nav: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
  software: 'M4 6h16v2H4V6zm0 5h16v2H4v-2zm0 5h16v2H4v-2z',
  sensor:
    'M12 2a7 7 0 00-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 00-7-7zm0 9.5a2.5 2.5 0 110-5 2.5 2.5 0 010 5z',
  cloud:
    'M19.35 10.04A7.49 7.49 0 0012 4C9.11 4 6.6 5.64 5.35 8.04A5.994 5.994 0 000 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z',
}

const category = computed(() => getCategoryById(activeCategoryId.value))

const activeFeature = computed(() => {
  const feats = category.value.features || []
  return feats[activeFeatureIdx.value] || feats[0] || { label: '', bullets: [] }
})

const activeGroupMeta = computed(() => {
  const group = productSidebar.find((g) => g.id === activeGroupId.value) || productSidebar[0]
  return {
    title: group.label,
    desc: groupDescriptions[group.id] || '',
  }
})

const groupVisual = computed(() => {
  const base = getGroupVisual(activeGroupId.value)
  return { ...base, tags: getGroupTags(activeGroupId.value) }
})

const activeGroupCategories = computed(() => {
  const group = productSidebar.find((g) => g.id === activeGroupId.value)
  return group ? group.children : []
})

const filteredProducts = computed(() => {
  const products = getMergedCategoryProducts(activeCategoryId.value)
  if (!activeTab.value || activeTab.value === '全部') return products
  const matched = products.filter((p) => p.tag === activeTab.value)
  return matched.length ? matched : products
})

const showShelfFab = computed(() => !CLIENT_DEMO_MODE)

function resolveGroupId(categoryId) {
  for (const group of productSidebar) {
    if (group.children.some((c) => c.id === categoryId)) return group.id
  }
  return 'system'
}

function syncFromRoute() {
  const cat = route.params.categoryId
  if (cat && getCategoryById(cat).id === cat) {
    activeCategoryId.value = cat
  } else {
    activeCategoryId.value = defaultCategoryId
  }
  activeGroupId.value = resolveGroupId(activeCategoryId.value)
  const tabs = getCategoryById(activeCategoryId.value).featureTabs
  const tabQuery = route.query.tab
  activeTab.value =
    typeof tabQuery === 'string' && tabs.includes(tabQuery) ? tabQuery : tabs[0]
  activeFeatureIdx.value = 0
}

function switchGroup(groupId) {
  activeGroupId.value = groupId
  const group = productSidebar.find((g) => g.id === groupId)
  if (group?.children.length) {
    selectCategory(group.children[0].id)
  }
}

function selectCategory(id) {
  if (activeCategoryId.value === id) return
  activeCategoryId.value = id
  activeTab.value = getCategoryById(id).featureTabs[0]
  activeFeatureIdx.value = 0
  router.replace({ name: 'products', params: { categoryId: id } })
}

function scrollToSection(id) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

function goHome() {
  router.push({ name: 'home' })
}

function isProductUiCover(product) {
  if (product.productKind === 'system') return true
  const src = product.image || ''
  return src.includes('/images/systems/')
}

function onProductImgError(e, product) {
  const fb = product.imageFallback
  if (fb && e.target.src !== fb) e.target.src = fb
}

function listCardImgStyle(product) {
  if (!product.listImageFocus) return undefined
  return {
    objectFit: 'cover',
    objectPosition: product.listImageFocus,
    padding: 0,
  }
}

function goProductDetail(product) {
  router.push({
    name: 'product-detail',
    params: {
      categoryId: activeCategoryId.value,
      productSlug: product.slug,
    },
  })
}

function goProductAdmin() {
  router.push({ name: 'product-admin' })
}

watch(() => [route.params.categoryId, route.query.tab], syncFromRoute, { immediate: true })

watch(activeCategoryId, () => {
  nextTick(refreshReveal)
})
</script>

<style scoped>
.pc-page {
  min-height: calc(100vh - 72px);
  width: 100%;
  padding-bottom: 0;
  overflow-x: hidden;
}

/* ── Sub nav ── */
.pc-subnav {
  background: transparent;
  border: none;
}

.pc-subnav__inner {
  max-width: min(1400px, 100%);
  margin: 0 auto;
  padding: 14px clamp(16px, 4vw, 72px);
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
}

.pc-breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
}

.pc-breadcrumb__link {
  color: #888;
  text-decoration: none;
  transition: color 0.2s;
}

.pc-breadcrumb__link:hover {
  color: var(--brand-primary);
}

.pc-breadcrumb__sep {
  color: #ccc;
}

.pc-breadcrumb__current {
  color: #333;
  font-weight: 500;
}

.pc-subnav__links {
  display: flex;
  gap: 28px;
}

.pc-subnav__link {
  font-size: 13px;
  color: #666;
  text-decoration: none;
  transition: color 0.2s;
}

.pc-subnav__link:hover {
  color: var(--brand-primary);
}

/* ── Top tabs ── */
.pc-tabs-bar {
  padding: 8px 0 16px;
  background: transparent;
  border: none;
}

.pc-tabs-bar__inner {
  max-width: var(--content-max-width);
  margin: 0 auto;
  padding: 0 var(--page-padding-x);
  justify-content: center;
}

.pc-tabs-bar__tab {
  position: relative;
  padding: 10px 20px;
  border: none;
  background: transparent;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-secondary);
  cursor: pointer;
  white-space: nowrap;
}

.pc-tabs-bar__tab--active::after {
  display: none;
}

.pc-page {
  width: 100%;
  padding-bottom: 0;
  overflow-x: hidden;
}

.pc-group-hero {
  width: 100%;
}

/* ── Category head ── */
.pc-category-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 22px;
  padding-bottom: 20px;
  border-bottom: 1px solid var(--border-soft);
}

.pc-category-head__eyebrow {
  display: inline-block;
  margin-bottom: 8px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 1.4px;
  text-transform: uppercase;
  color: var(--brand-primary);
}

.pc-category-head__title {
  margin: 0;
  font-size: 26px;
  font-weight: 800;
  line-height: 1.25;
  color: var(--text-primary);
}

.pc-category-head__desc {
  margin: 12px 0 0;
  max-width: 760px;
  font-size: 14px;
  line-height: 1.8;
  color: var(--text-secondary);
}

.pc-category-head__meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 10px;
  flex-shrink: 0;
}

.pc-category-head__count {
  padding: 8px 14px;
  border-radius: var(--radius-pill);
  background: rgba(13, 79, 110, 0.08);
  font-size: 13px;
  font-weight: 700;
  color: var(--brand-primary);
}

.pc-category-head__link {
  font-size: 13px;
  font-weight: 600;
  color: var(--brand-primary);
  text-decoration: none;
}

.pc-category-head__link:hover {
  text-decoration: underline;
}

/* ── Feature panel ── */
.pc-feature-panel {
  margin-bottom: 28px;
}

.pc-feature-panel__head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.pc-feature-panel__title {
  margin: 6px 0 0;
  font-size: 20px;
  font-weight: 800;
  color: var(--text-primary);
}

.pc-feature-panel__hint {
  margin: 0;
  max-width: 360px;
  font-size: 13px;
  line-height: 1.6;
  color: var(--text-muted);
  text-align: right;
}

.pc-feature-body {
  border-radius: var(--radius-lg);
  background: var(--surface-card);
  border: 1px solid var(--border-soft);
  box-shadow: var(--shadow-card);
  overflow: hidden;
}

.pc-feature-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 16px 18px;
  background: linear-gradient(180deg, #f8fbff 0%, #f2f7fc 100%);
  border-bottom: 1px solid var(--border-soft);
}

.pc-feature-tab {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: 1px solid transparent;
  border-radius: var(--radius-pill);
  background: transparent;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary);
  cursor: pointer;
  transition: background 0.25s, color 0.25s, border-color 0.25s, box-shadow 0.25s;
}

.pc-feature-tab:hover {
  background: rgba(255, 255, 255, 0.8);
  color: var(--brand-primary);
}

.pc-feature-tab--active {
  background: #fff;
  border-color: rgba(13, 79, 110, 0.18);
  color: var(--brand-primary);
  box-shadow: var(--shadow-sm);
}

.pc-feature-tab__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(13, 79, 110, 0.08);
  color: inherit;
}

.pc-feature-content {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 200px;
  gap: 24px;
  padding: 24px 26px 26px;
  animation: fadeIn 0.28s var(--ease-out-expo);
}

.pc-feature-content__title {
  margin: 0 0 12px;
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
}

.pc-feature-content__desc {
  margin: 0 0 16px;
  font-size: 14px;
  line-height: 1.85;
  color: var(--text-secondary);
}

.pc-feature-bullets {
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 18px;
}

.pc-feature-bullets li {
  position: relative;
  padding-left: 16px;
  font-size: 13px;
  line-height: 1.7;
  color: #344055;
}

.pc-feature-bullets li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 9px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--brand-primary);
}

.pc-feature-aside {
  padding: 16px;
  border-radius: var(--radius-md);
  background: linear-gradient(180deg, #f4f9fc 0%, #eef6fb 100%);
  border: 1px solid rgba(13, 79, 110, 0.1);
}

.pc-feature-aside__label {
  margin: 0 0 12px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: var(--brand-primary);
}

.pc-feature-tags {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.pc-feature-tags li {
  padding: 6px 10px;
  border-radius: var(--radius-pill);
  background: #fff;
  border: 1px solid rgba(13, 79, 110, 0.12);
  font-size: 12px;
  font-weight: 600;
  color: #2d4a63;
}

/* ── Product explorer ── */
.pc-explorer {
  padding: 32px 0 64px;
}

.pc-explorer__inner {
  max-width: min(1400px, 100%);
  margin: 0 auto;
  padding: 0 clamp(16px, 4vw, 72px);
  width: 100%;
  box-sizing: border-box;
  display: flex;
  gap: 0;
  align-items: flex-start;
}

.pc-filter-sidebar {
  width: 220px;
  flex-shrink: 0;
  margin-right: 28px;
  border-radius: var(--radius-lg);
  background: var(--surface-card);
  border: 1px solid var(--border-soft);
  box-shadow: var(--shadow-card);
  overflow: hidden;
}

.pc-compare-btn {
  display: block;
  width: 100%;
  padding: 14px 16px;
  border: none;
  background: var(--gradient-brand);
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  text-align: center;
  transition: opacity 0.25s, transform 0.25s;
}

.pc-compare-btn:hover {
  opacity: 0.92;
  transform: translateY(-1px);
}

.pc-filter-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.pc-filter-item {
  display: block;
  width: 100%;
  padding: 13px 18px 13px 20px;
  border: none;
  background: transparent;
  font-size: 13px;
  color: var(--text-secondary);
  text-align: left;
  cursor: pointer;
  border-left: 3px solid transparent;
  transition:
    background 0.2s,
    color 0.2s,
    border-color 0.2s;
  line-height: 1.4;
}

.pc-filter-item:hover {
  background: rgba(13, 79, 110, 0.04);
  color: var(--brand-primary);
}

.pc-filter-item--active {
  background: rgba(13, 79, 110, 0.06);
  color: var(--brand-primary);
  font-weight: 700;
  border-left-color: var(--brand-primary);
}

.pc-product-area {
  flex: 1;
  min-width: 0;
}

.pc-subtabs {
  display: flex;
  gap: 24px;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid #dde0e4;
}

.pc-subtab {
  border: none;
  background: none;
  font-size: 13px;
  color: #888;
  cursor: pointer;
  padding: 4px 0;
  position: relative;
  transition: color 0.2s;
}

.pc-subtab--active {
  color: var(--brand-primary);
  font-weight: 600;
}

.pc-subtab--active::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: -13px;
  height: 2px;
  background: var(--brand-primary);
}

.pc-product-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

/* ── Product card：上图 + 标题 + 描述 + 平台名 ── */
.pc-card {
  background: var(--surface-card);
  border: 1px solid var(--border-soft);
  border-radius: var(--radius-lg);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: var(--shadow-sm);
  transition:
    box-shadow 0.35s var(--ease-out-expo),
    transform 0.35s var(--ease-out-expo),
    border-color 0.25s;
}

.pc-card:hover {
  box-shadow: 0 10px 32px rgba(17, 39, 78, 0.12);
  transform: translateY(-3px);
  border-color: rgba(43, 89, 195, 0.22);
}

.pc-card__media {
  position: relative;
  height: 168px;
  flex-shrink: 0;
  background: linear-gradient(180deg, #f6f8fb 0%, #eceff4 100%);
  display: flex;
  align-items: stretch;
  justify-content: center;
  overflow: hidden;
}

.pc-card--ui .pc-card__media {
  height: 176px;
  background: linear-gradient(160deg, #f0f4fa 0%, #e4ecf6 100%);
}

.pc-card__img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center center;
  padding: 12px 14px;
  box-sizing: border-box;
  transition: transform 0.45s var(--ease-out-expo);
}

.pc-card--ui .pc-card__img {
  object-fit: contain;
  object-position: top center;
  padding: 8px 10px;
  box-sizing: border-box;
}

.pc-card:hover .pc-card__img {
  transform: scale(1.03);
}

.pc-card__body {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 14px 16px 16px;
  min-height: 132px;
}

.pc-card__name {
  margin: 0 0 8px;
  font-size: 15px;
  font-weight: 700;
  color: #1a1d24;
  line-height: 1.4;
}

.pc-card__desc {
  margin: 0 0 auto;
  font-size: 12px;
  color: #8a9199;
  line-height: 1.55;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.pc-card__model {
  margin: 12px 0 0;
  padding-top: 10px;
  border-top: 1px solid #eef0f3;
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
  line-height: 1.35;
}

.pc-shelf-fab {
  position: fixed;
  right: 28px;
  bottom: 88px;
  z-index: 320;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 20px;
  border: none;
  border-radius: var(--radius-pill);
  background: var(--gradient-brand);
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  box-shadow: var(--shadow-brand-lg);
  cursor: pointer;
  transition: transform 0.25s var(--ease-premium), box-shadow 0.25s;
}

.pc-shelf-fab:hover {
  transform: translateY(-2px);
  box-shadow: 0 20px 44px rgba(13, 79, 110, 0.34);
}

.pc-shelf-fab__icon {
  font-size: 18px;
  line-height: 1;
}

@media (max-width: 1100px) {
  .pc-feature-content {
    grid-template-columns: 1fr;
  }

  .pc-feature-bullets {
    grid-template-columns: 1fr;
  }

  .pc-product-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .pc-hero-banner__inner,
  .pc-subnav__inner,
  .pc-tabs-bar__inner,
  .pc-explorer__inner {
    padding-left: 16px;
    padding-right: 16px;
  }

  .pc-category-head {
    flex-direction: column;
  }

  .pc-category-head__meta {
    align-items: flex-start;
  }

  .pc-feature-panel__head {
    flex-direction: column;
    align-items: flex-start;
  }

  .pc-feature-panel__hint {
    max-width: none;
    text-align: left;
  }

  .pc-tabs-bar__inner {
    gap: 20px;
    overflow-x: auto;
    justify-content: flex-start;
  }

  .pc-feature-tabs {
    flex-wrap: nowrap;
    overflow-x: auto;
    padding-bottom: 12px;
  }

  .pc-feature-content {
    padding: 18px;
  }

  .pc-explorer__inner {
    flex-direction: column;
  }

  .pc-filter-sidebar {
    width: 100%;
    margin-right: 0;
    margin-bottom: 20px;
  }

  .pc-product-grid {
    grid-template-columns: 1fr;
  }

  .pc-hero-banner {
    height: 240px;
  }

  .pc-hero-banner__title {
    font-size: 36px;
  }
}
</style>
